// src/pages/Employees.js
import { useState } from "react";
import { addEmployee, updateEmployee, deleteEmployee } from "../firebase/services";
import { useAuth } from "../context/AuthContext";
import { Btn, Tag, Card, Modal, Avatar, PageHeader, Alert, Spinner } from "../components/UI";
import EmployeeForm from "../components/EmployeeForm";

const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function Employees({ employees, setEmployees, filterStatus }) {
  const { canEdit, canDelete } = useAuth();
  const [search, setSearch]     = useState("");
  const [dept, setDept]         = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editEmp, setEditEmp]   = useState(null);
  const [viewEmp, setViewEmp]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg]           = useState("");

  const filtered = employees.filter(e => {
    if (filterStatus && e.status !== filterStatus) return false;
    if (dept !== "all" && e.dept !== dept) return false;
    const q = search.toLowerCase();
    return !q || e.name?.toLowerCase().includes(q) || e.role?.toLowerCase().includes(q) || e.dept?.toLowerCase().includes(q);
  });

  const depts = ["all", ...new Set(employees.map(e => e.dept).filter(Boolean))];

  const title = filterStatus === "current" ? "Current Staff"
    : filterStatus === "past" ? "Past Employees"
    : "All Employees";

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3500); };

  const save = async (emp) => {
    const isNew = !employees.find(e => e.id === emp.id);
    if (isNew) {
      const { id: tmpId, ...data } = emp;
      const realId = await addEmployee(data);
      setEmployees(prev => [...prev, { ...data, id: realId }]);
      flash("✅ Employee added successfully.");
    } else {
      const { id, ...data } = emp;
      await updateEmployee(id, data);
      setEmployees(prev => prev.map(e => e.id === id ? emp : e));
      flash("✅ Employee updated.");
    }
    setShowForm(false);
    setEditEmp(null);
  };

  const del = async (id) => {
    setDeleting(true);
    await deleteEmployee(id);
    setEmployees(prev => prev.filter(e => e.id !== id));
    setConfirmDel(null);
    setViewEmp(null);
    setDeleting(false);
    flash("🗑 Employee deleted.");
  };

  return (
    <div style={{ animation: "fadeUp .35s ease" }}>
      <PageHeader
        title={title}
        sub={`${filtered.length} employee${filtered.length !== 1 ? "s" : ""} found`}
        action={canEdit && <Btn onClick={() => { setEditEmp(null); setShowForm(true); }}>+ Add Employee</Btn>}
      />

      {msg && <Alert type={msg.startsWith("✅") ? "success" : "warn"} style={{ marginBottom: 16 }}>{msg}</Alert>}

      {/* Filters */}
      <Card style={{ padding: "14px 18px", marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search name, role, department…"
          style={{ padding: "8px 14px", border: "1.5px solid #d4d0c8", borderRadius: 8, fontSize: 14, flex: 1, minWidth: 200, outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
        <select value={dept} onChange={e => setDept(e.target.value)}
          style={{ padding: "8px 12px", border: "1.5px solid #d4d0c8", borderRadius: 8, fontSize: 14, background: "#fff", fontFamily: "'DM Sans',sans-serif" }}>
          {depts.map(d => <option key={d} value={d}>{d === "all" ? "All Departments" : d}</option>)}
        </select>
      </Card>

      {/* Table */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--cream)", fontSize: 11, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6 }}>
                {["Employee", "Role", "Dept", "Joined", "Phone", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 48, textAlign: "center", color: "var(--slate-light)", fontSize: 14 }}>No employees match your filters.</td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.id} style={{ borderTop: "1px solid #f0ece3", transition: "background .15s" }}
                  onMouseEnter={ev => ev.currentTarget.style.background = "#faf8f3"}
                  onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "11px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={e.name} size={34} photo={e.photoURL} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{e.name}</div>
                        <div style={{ fontSize: 11, color: "var(--slate-light)" }}>{e.email || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "11px 16px", fontSize: 13 }}>{e.role || "—"}</td>
                  <td style={{ padding: "11px 16px", fontSize: 13 }}>{e.dept || "—"}</td>
                  <td style={{ padding: "11px 16px", fontSize: 13, whiteSpace: "nowrap" }}>{fmt(e.joined)}</td>
                  <td style={{ padding: "11px 16px", fontSize: 13 }}>{e.phone || "—"}</td>
                  <td style={{ padding: "11px 16px" }}>
                    <Tag label={e.status === "current" ? "Active" : "Past"} color={e.status === "current" ? "var(--success)" : "var(--slate-light)"} />
                  </td>
                  <td style={{ padding: "11px 16px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn size="xs" variant="ghost" onClick={() => setViewEmp(e)}>View</Btn>
                      {canEdit && <Btn size="xs" variant="secondary" onClick={() => { setEditEmp(e); setShowForm(true); }}>Edit</Btn>}
                      {canDelete && <Btn size="xs" variant="danger" onClick={() => setConfirmDel(e.id)}>Del</Btn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add / Edit modal */}
      <Modal open={showForm} onClose={() => { setShowForm(false); setEditEmp(null); }}
        title={editEmp ? "Edit Employee" : "Add New Employee"} width={660}>
        <EmployeeForm emp={editEmp} onSave={save} onClose={() => { setShowForm(false); setEditEmp(null); }} />
      </Modal>

      {/* View modal */}
      <Modal open={!!viewEmp} onClose={() => setViewEmp(null)} title="Employee Profile" width={560}>
        {viewEmp && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <Avatar name={viewEmp.name} size={76} photo={viewEmp.photoURL} />
              <div>
                <h3 style={{ fontSize: 20, fontFamily: "'Syne',sans-serif" }}>{viewEmp.name}</h3>
                <div style={{ color: "var(--slate-light)", fontSize: 14, marginTop: 2 }}>{viewEmp.role || "—"} · {viewEmp.dept || "—"}</div>
                <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                  <Tag label={viewEmp.status === "current" ? "Active" : "Past"} color={viewEmp.status === "current" ? "var(--success)" : "var(--slate-light)"} />
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                ["Date of Birth", fmt(viewEmp.dob)],
                ["Joined", fmt(viewEmp.joined)],
                ["Phone", viewEmp.phone],
                ["Email", viewEmp.email],
                ["Salary", viewEmp.salary ? "₹" + Number(viewEmp.salary).toLocaleString("en-IN") : "—"],
                ["Address", viewEmp.address],
              ].map(([k, v]) => (
                <div key={k} style={{ background: "var(--cream)", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6, marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, wordBreak: "break-word" }}>{v || "—"}</div>
                </div>
              ))}
            </div>

            {viewEmp.notes && (
              <div style={{ background: "#fff8ee", border: "1px solid #f0ddb0", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                <b>Notes:</b> {viewEmp.notes}
              </div>
            )}

            {(viewEmp.attachments || []).length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6, marginBottom: 8 }}>
                  Attachments ({viewEmp.attachments.length})
                </div>
                {viewEmp.attachments.map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--cream)", borderRadius: 8, marginBottom: 6 }}>
                    <span>📄</span>
                    <a href={a.url} target="_blank" rel="noreferrer"
                      style={{ flex: 1, color: "var(--slate)", fontSize: 13, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.name}
                    </a>
                    <span style={{ fontSize: 11, color: "var(--slate-light)", flexShrink: 0 }}>{(a.size / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              {canDelete && <Btn variant="danger" size="sm" onClick={() => setConfirmDel(viewEmp.id)}>Delete</Btn>}
              {canEdit && <Btn variant="secondary" size="sm" onClick={() => { setEditEmp(viewEmp); setViewEmp(null); setShowForm(true); }}>Edit</Btn>}
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm delete */}
      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Confirm Delete" width={400}>
        <p style={{ marginBottom: 20, fontSize: 14 }}>This will permanently delete the employee record and all associated data. This cannot be undone.</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setConfirmDel(null)} disabled={deleting}>Cancel</Btn>
          <Btn variant="danger" onClick={() => del(confirmDel)} disabled={deleting}>
            {deleting ? <><Spinner size={13} color="#fff" /> Deleting…</> : "Yes, Delete"}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
